import type { CardListItem, CardListResponse, CardDetails, CardBalance } from '@/types/shared'

export const useCardsStore = defineStore('cards_store', {
	state: () => ({
		cards: [] as CardListItem[],
		selected_card: null as CardListItem | null,
		selected_card_details: null as CardDetails | null,
		selected_card_balance: null as CardBalance | null,
		is_loading: false,
		is_details_loading: false,
		is_balance_loading: false,
		is_transactions_loading: false,
	}),

	getters: {
		active_cards(): CardListItem[] {
			return this.cards.filter(card => card.status === 'active')
		},
		has_active_cards(): boolean {
			return this.active_cards.length > 0
		}
	},

	actions: {
		async fetch_cards() {
			if (this.is_loading) return

			this.is_loading = true
			try {
				const api = useApi()
				const response: CardListResponse = await api.post('/api/hybrid/cards/list')

				if (response.success) {
					this.cards = response.cards.sort((a, b) => {
						if (a.status === 'active' && b.status !== 'active') return -1
						if (a.status !== 'active' && b.status === 'active') return 1
						return 0
					})

					// Auto-select first card if none selected
					if (!this.selected_card && this.cards.length > 0) {
						this.selected_card = this.cards[0]
					}
				}
			} catch (error) {
				console.error('Error fetching cards:', error)
			} finally {
				this.is_loading = false
			}
		},

		async fetch_card_details(identifier: string, is_issue_order_id = false, signal?: AbortSignal) {
			if (this.is_details_loading) return

			this.is_details_loading = true
			try {
				const api = useApi()
				const payload = is_issue_order_id ? { issue_order_id: identifier } : { cs_id: identifier }
				const response: any = await api.post('/api/hybrid/cards/details', payload, { signal })

				if (response.success) {
					const auth_store = useAuthStore()
					this.selected_card_details = JSON.parse(
						await auth_store.decrypt_data_with_client_private_key_and_otk(
							response.details,
							response.otk
						)
					)
				}
			} catch (error) {
				if ((error as any)?.name !== 'AbortError') {
					console.error('Error fetching card details:', error)
				}
			} finally {
				this.is_details_loading = false
			}
		},

		async fetch_card_balance(identifier: string, is_issue_order_id = false, signal?: AbortSignal) {
			if (this.is_balance_loading) return

			this.is_balance_loading = true
			try {
				const api = useApi()
				const payload = is_issue_order_id ? { issue_order_id: identifier } : { cs_id: identifier }
				const response: any = await api.post('/api/hybrid/cards/balance', payload, { signal })

				if (response.success) {
					this.selected_card_balance = response.item
				}
			} catch (error) {
				if ((error as any)?.name !== 'AbortError') {
					console.error('Error fetching card balance:', error)
				}
			} finally {
				this.is_balance_loading = false
			}
		},

		async topup_card(identifier: string, amount: number, is_issue_order_id = false) {
			try {
				const api = useApi()
				const payload = is_issue_order_id ? { issue_order_id: identifier } : { cs_id: identifier }
				const response: any = await api.post('/api/hybrid/cards/topup', { ...payload, amount })

				if (response.success) {
					const auth_store = useAuthStore()
					auth_store.user = response.user
					return response
				}
			} catch (error) {
				console.error('Error topping up card:', error)
				throw error
			}
		},

		select_card(card: CardListItem) {
			this.selected_card = card
			this.selected_card_details = null
			this.selected_card_balance = null
		},

		clear_selected_card() {
			this.selected_card = null
			this.selected_card_details = null
			this.selected_card_balance = null
		}
	}
})